import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTimelineContext } from "../contexts/TimelineContext";
import { timelineData } from "./consts/timelineConstants";
import {
  CategoryButtonsProps,
  TimelineData,
  TimelineItemProps,
} from "./models/timelineModels";
import { STANDARD_VARIANTS } from "../constants/animations";
import { CONTAINER_STYLES } from "../constants/styles";

const CategoryButtons: React.FC<CategoryButtonsProps> = ({
  activeCategory,
  setActiveCategory,
}) => {
  const categories = ["Work", "Projects", "Education", "Misc"];

  return (
    <div className="flex justify-center mb-8">
      <div className="bg-surface-200 dark:bg-accent-800 rounded-xl p-1 shadow-inner flex flex-wrap justify-center">
        {categories.map((category, index) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category.toLowerCase())}
            className={`md:px-6 md:py-3 px-4 py-2 text-lg font-medium rounded-lg transition-all duration-200 ease-out relative
              ${activeCategory === category.toLowerCase()
                ? "bg-white dark:bg-accent-700 text-primary-600 dark:text-primary-300 shadow-sm"
                : "text-accent-600 dark:text-accent-300 hover:text-accent-900 dark:hover:text-accent-100"
              }`}
          >
            {category}
          </button>
        ))}
      </div>
    </div>
  );
};

const Timeline: React.FC = () => {
  const { activeCategory, setActiveCategory } = useTimelineContext();
  const [isVisible, setIsVisible] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);

  const sortedTimelineData = useMemo(() => {
    const sortedData: TimelineData = {};
    Object.keys(timelineData).forEach((category) => {
      sortedData[category] = timelineData[category].sort((a, b) => {
        if (a.weight !== null && b.weight != null) {
          return b.weight - a.weight;
        } else if (a.weight !== null) {
          return -1;
        } else if (b.weight !== null) {
          return 1;
        }
        const endYearA =
          a.endYear === "Present" ? new Date().getFullYear() + 1 : a.endYear;
        const endYearB =
          b.endYear === "Present" ? new Date().getFullYear() + 1 : b.endYear;
        return Number(endYearB) - Number(endYearA);
      });
    });
    return sortedData;
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (timelineRef.current) {
      observer.observe(timelineRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`${CONTAINER_STYLES.content} p-8 font-sans relative z-10 py-20`}
      ref={timelineRef}
    >
      <motion.div
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={STANDARD_VARIANTS.itemLarge}
        className="flex flex-col items-center"
      >
        {/* Keeps the heading outline intact (h1 hero -> h2 section -> h3 items)
            without adding a visible title to the tabbed timeline. */}
        <h2 className="sr-only">Experience</h2>
        <CategoryButtons
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
        />
        <div className="relative flex w-full">
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-accent-300 dark:bg-accent-700 duration-300"></div>
          <div className="grow ml-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                {sortedTimelineData[activeCategory].map((item, index) => (
                  <TimelineItem
                    key={index}
                    item={item}
                    isProject={activeCategory === "projects"}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const TimelineItem: React.FC<TimelineItemProps> = ({
  item,
  isProject = false,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  useEffect(() => {
    if (isProject && item.imageSrc) {
      import(`../images/icons/${item.imageSrc}`)
        .then((image) => {
          setImageSrc(image.default);
        })
        .catch((err) => console.error("Error loading image:", err));
    }
  }, [isProject, item.imageSrc]);

  const parseBoldText = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="relative mb-8 p-2">
      {/* Node dot tying this entry to the timeline's vertical line
          (line center sits 31px left of the item edge; dot is 12px wide) */}
      <span
        aria-hidden="true"
        className="absolute top-4 left-[-37px] h-3 w-3 rounded-full bg-primary-500 dark:bg-primary-400"
      />
      <div className="flex items-center">
        {isProject && imageSrc && (
          <motion.img
            src={imageSrc}
            alt={`${item.title} icon`}
            className="w-12 h-12 mr-4 rounded-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          />
        )}
        <div className="flex flex-col justify-center">
          <h3 className="text-xl text-accent-800 dark:text-accent-200 mb-1 font-semibold">
            {item.title}
          </h3>
          <h4 className="text-md text-accent-600 dark:text-accent-400">
            {parseBoldText(item.subtitle)}
            <span className="text-sm text-accent-500 dark:text-accent-500">
              {" | "}
              {item.startYear === item.endYear
                ? item.startYear
                : `${item.startYear} - ${item.endYear}`}
            </span>
          </h4>
        </div>
      </div>
      <div>
        <div>
          {item.links.length > 0 && (
            <div className="mt-2">
              {item.links.map((link, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <span className="text-accent-500 mx-2">|</span>}
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 font-semibold hover:underline transition-colors duration-200"
                  >
                    {link.text}
                  </a>
                </React.Fragment>
              ))}
            </div>
          )}
          <div className="mt-2 space-y-2 max-w-prose">
            {item.bulletPoints.map((point, index) => (
              <div key={index} className="flex items-start ml-3">
                <span className="text-primary-600 dark:text-primary-400 mr-2 text-xs mt-1.5">►</span>
                <span className="text-accent-700 dark:text-accent-300">
                  {parseBoldText(point)}
                </span>
              </div>
            ))}
          </div>
          {item.technologiesUsed.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {item.technologiesUsed.map((tech, index) => (
                <span
                  key={index}
                  className="inline-block duration-300 bg-surface-200 dark:bg-accent-700 rounded-full px-3 py-1 text-xs font-semibold text-accent-800 dark:text-accent-300"
                >
                  {tech}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Timeline;
